import { Badge, Button, Card, Group, Modal, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBuilding, IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { api, unwrap } from '../api.ts';
import { nextBuildingCode } from '../format.ts';
import { Empty, Failure, Loading, Page, useMe, useSave } from '../ui.tsx';

function NewBuilding(props: { codes: string[]; onDone: () => void }) {
  const [form, setForm] = useState({
    code: nextBuildingCode(props.codes),
    name: '',
    timeZone: 'Europe/Prague',
    address: '',
  });
  const create = useSave({
    run: () =>
      unwrap(
        api().POST('/v1/buildings', {
          body: {
            code: form.code,
            name: form.name,
            timeZone: form.timeZone,
            ...(form.address ? { address: form.address } : {}),
          },
        }),
      ),
    invalidates: [['buildings']],
    success: (building) => `${building.name} created`,
    onDone: props.onDone,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(undefined);
  };
  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Code"
          description="B and four digits; the start of every place code in the building"
          required
          value={form.code}
          onChange={(event) => setForm({ ...form, code: event.currentTarget.value.toUpperCase() })}
        />
        <TextInput
          label="Name"
          required
          data-autofocus
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
        />
        <TextInput
          label="Address"
          value={form.address}
          onChange={(event) => setForm({ ...form, address: event.currentTarget.value })}
        />
        <TextInput
          label="Time zone"
          description="Booking rules are judged in it"
          required
          value={form.timeZone}
          onChange={(event) => setForm({ ...form, timeZone: event.currentTarget.value })}
        />
        <Failure error={create.error} />
        <Button type="submit" loading={create.isPending}>
          Create building
        </Button>
      </Stack>
    </form>
  );
}

export function BuildingsPage() {
  const me = useMe();
  const buildings = useQuery({ queryKey: ['buildings'], queryFn: () => unwrap(api().GET('/v1/buildings')) });
  const [creating, create] = useDisclosure(false);
  const own = (organizationId: string) => organizationId === me.data?.organizationId;
  return (
    <Page
      title="Buildings"
      description="Buildings your organization owns, and those it has a contract in."
      actions={
        <Button leftSection={<IconPlus size={16} />} onClick={create.open}>
          New building
        </Button>
      }
    >
      {buildings.isPending ? <Loading /> : null}
      <Failure error={buildings.error} />
      {buildings.data?.length === 0 ? <Empty>No buildings yet.</Empty> : null}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {buildings.data?.map((building) => (
          <Link
            key={building.id}
            to="/buildings/$buildingId"
            params={{ buildingId: building.id }}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <Card withBorder padding="lg" h="100%">
              <Group justify="space-between" align="flex-start">
                <IconBuilding size={28} stroke={1.5} />
                <Badge variant="light" color={own(building.organizationId) ? 'ink' : 'gray'}>
                  {own(building.organizationId) ? 'Owned' : 'Contract'}
                </Badge>
              </Group>
              <Text fw={700} mt="md">
                {building.name}
              </Text>
              <Text size="sm" c="dimmed">
                {building.code} · {building.address ?? building.timeZone}
              </Text>
            </Card>
          </Link>
        ))}
      </SimpleGrid>
      <Modal opened={creating} onClose={create.close} title="New building" centered>
        <NewBuilding codes={buildings.data?.map((building) => building.code) ?? []} onDone={create.close} />
      </Modal>
    </Page>
  );
}
